/**
 * isEventDeliveredTask
 * --------------------
 * Single source of truth for "is this task delivered via an event slot
 * (calendar booking, video meeting, oral exam, etc.)?".
 *
 * Background: historically the frontend gated all booking UI on
 * `task.type === 'event'`. After oral exams were promoted to a
 * first-class `TaskType.oral_exam` (still carrying an `eventConfig`),
 * those checks silently dropped oral exams from every booking-related
 * surface. The backend already exposes the derived `isDeliveredAsEvent`
 * flag (true whenever `event_config.event_kind_id` is set), so we use
 * that as the canonical signal and keep a structural fallback for
 * resilience against stale clients/tests.
 */
type MinimalTask = {
  isDeliveredAsEvent?: boolean;
  eventConfig?: { eventKindId?: string } | null;
} | null | undefined;

export const isEventDeliveredTask = (task: MinimalTask): boolean => {
  if (!task) return false;
  if (task.isDeliveredAsEvent) return true;
  return Boolean(task.eventConfig?.eventKindId);
};

/**
 * isMeetingOnlyTask
 * -----------------
 * True when the student's graded work happens in a live session (video meeting,
 * oral exam room) rather than a TaskScreen composer. Oral exams still open
 * TaskScreen as a question-pool prep surface.
 *
 * Contrast with isEventDeliveredTask, which is true for any task that
 * requires a booking slot — including proctored MCQs, which still open
 * in TaskScreen for MCQComposer to render the quiz.
 */
export const isMeetingOnlyTask = (
  task: (MinimalTask & { type?: string }) | null | undefined,
): boolean => {
  if (!task) return false;
  return task.type === 'event' || task.type === 'oral_exam';
};
